import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  PhoneIncoming,
  ShieldAlert,
  UserRound,
  X,
} from 'lucide-react';
import { isPartnerSalesBlocked } from '@erp/shared';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/features/auth';
import {
  useActiveCalls,
  useMyTelephonyConfig,
  useScreenPop,
} from '@/features/telephony/api/use-telephony';
import { formatDateTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/**
 * Blendet bei einem klingelnden Ruf die Kontaktkarte des Gesprächspartners ein.
 * Die Einblendung erscheint nur, wenn der Benutzer sie in seiner Telefonie-
 * Konfiguration aktiviert hat.
 */
export function ScreenPopOverlay(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const canUse = canAccess('TELEPHONY', 'USE');

  const config = useMyTelephonyConfig(canUse);
  const isEnabled = canUse && config.data?.isEnabled === true;

  const activeCalls = useActiveCalls(isEnabled && config.data?.autoOpenScreenPop === true);
  const call = activeCalls.data?.[0] ?? null;
  const counterpart =
    call === null ? null : call.direction === 'INBOUND' ? call.callerNumber : call.calleeNumber;

  const [dismissedCallId, setDismissedCallId] = useState<string | null>(null);
  const screenPop = useScreenPop(call === null || call.id === dismissedCallId ? null : counterpart);

  useEffect(() => {
    if (call === null) {
      setDismissedCallId(null);
    }
  }, [call]);

  if (call === null || counterpart === null || call.id === dismissedCallId) {
    return null;
  }

  const partner = screenPop.data ?? null;
  const blocked = partner !== null && partner.partnerId !== null && isPartnerSalesBlocked(partner);

  return (
    <aside
      role="status"
      aria-live="assertive"
      className={cn(
        'fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-lg border bg-background p-4 shadow-lg',
        blocked ? 'border-destructive ring-2 ring-destructive/40' : 'border-border',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-medium">
          <PhoneIncoming className="h-4 w-4" aria-hidden="true" />
          {t('telephony.screenPop.title')}
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.close')}
          onClick={() => setDismissedCallId(call.id)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {blocked && partner !== null ? (
          <Alert variant="destructive">
            <ShieldAlert aria-hidden="true" className="h-4 w-4 shrink-0 text-destructive" />
            <div className="flex flex-col gap-0.5">
              <AlertTitle className="text-destructive">
                {t('telephony.screenPop.blockedTitle', {
                  status: t(`partners.riskStatuses.${partner.riskStatus}`),
                })}
              </AlertTitle>
              <AlertDescription className="text-foreground">
                {t('telephony.screenPop.blockedReason', { reason: partner.blockReason ?? '—' })}
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          {call.direction === 'INBOUND' ? (
            <ArrowDownLeft className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="font-mono">{counterpart}</span>
        </span>

        {partner === null || partner.partnerId === null ? (
          <p className="text-sm text-muted-foreground">{t('telephony.screenPop.unknown')}</p>
        ) : (
          <>
            <p className="flex items-center gap-2 text-base font-semibold">
              <UserRound className="h-4 w-4 shrink-0" aria-hidden="true" />
              {partner.partnerName ?? partner.partnerNumber ?? ''}
            </p>
            {partner.contactName === null ? null : (
              <p className="text-sm text-muted-foreground">{partner.contactName}</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {partner.partnerNumber === null ? null : (
                <Badge variant="outline">{partner.partnerNumber}</Badge>
              )}
              <Badge variant="secondary">
                {t('telephony.screenPop.openDocuments', { count: partner.openDocumentCount })}
              </Badge>
            </div>
            {partner.lastCallAt === null ? null : (
              <p className="text-xs text-muted-foreground">
                {t('telephony.screenPop.lastCall', {
                  timestamp: formatDateTime(partner.lastCallAt, i18n.language),
                })}
              </p>
            )}
            <Button
              size="sm"
              className="mt-1"
              onClick={() => {
                setDismissedCallId(call.id);
                navigate(partner.partnerUrl ?? `/partners/${partner.partnerId ?? ''}`);
              }}
            >
              {t('telephony.screenPop.open')}
            </Button>
          </>
        )}
      </div>
    </aside>
  );
}
